import Phaser from 'phaser';
import { TREBUCHET } from '../../config';
import { Enemy } from '../Enemy';
import { Projectile } from '../Projectile';
import { StickFigureGraphics } from '../../rendering/StickFigureGraphics';
import { BuildingSlot, ChainUpgradeEffect, TargetPriority } from '../../types';
import { selectByPriority } from '../units/BaseUnit';

export class Trebuchet {
    scene: Phaser.Scene;
    x: number;
    y: number;
    level: number;
    position: BuildingSlot;
    unitType: string = 'trebuchet';
    graphics: Phaser.GameObjects.Graphics;
    sprite: Phaser.GameObjects.Sprite;
    private attackTimer: number = 0;
    private projectiles: Projectile[];
    stackCount: number = 1;

    // Chain upgrade bonuses
    bonusDamageMult: number = 1;
    bonusAoeRadius: number = 0;
    globalDamageMult: number = 1;
    attackSpeedMult: number = 1;

    constructor(
        scene: Phaser.Scene,
        x: number,
        y: number,
        position: BuildingSlot,
        projectiles: Projectile[]
    ) {
        this.scene = scene;
        this.x = x;
        this.y = y;
        this.level = 1;
        this.position = position;
        this.graphics = scene.add.graphics();
        this.sprite = scene.add.sprite(x, y, 'building_trebuchet');
        this.sprite.setOrigin(0.5, 110 / 128);
        this.projectiles = projectiles;
        this.stackCount = 1;
        this.draw();
    }

    getDamage(): number {
        return TREBUCHET.baseDamage * this.stackCount * this.bonusDamageMult * this.globalDamageMult;
    }

    getAoeRadius(): number {
        return Math.min(TREBUCHET.aoeRadius + this.bonusAoeRadius, 300);
    }

    addStack(): void {
        this.stackCount++;
    }

    applyChainUpgrade(effect: ChainUpgradeEffect): void {
        if (effect.damageMultBonus !== undefined) this.bonusDamageMult *= (1 + effect.damageMultBonus);
        if (effect.aoeRadiusBonus !== undefined) this.bonusAoeRadius += effect.aoeRadiusBonus;
        this.level++;
        this.draw();
    }

    update(time: number, delta: number, enemies: Enemy[]): void {
        this.attackTimer -= delta;
        if (this.attackTimer <= 0) {
            const target = this.findTarget(enemies);
            if (target) {
                this.attackTimer = TREBUCHET.attackInterval / this.attackSpeedMult;
                this.fire(target);
            }
        }
        this.draw();
    }

    private findTarget(enemies: Enemy[]): Enemy | null {
        const range = TREBUCHET.range;
        const candidates: Enemy[] = [];
        for (const enemy of enemies) {
            if (enemy.isDead) continue;
            if (enemy.enemyType === 'boulder') continue;
            if (enemy.isBurrowed) continue;
            if (enemy.isGoblin) continue;
            const dx = enemy.x - this.x;
            const dy = enemy.y - this.y;
            if (Math.sqrt(dx * dx + dy * dy) < range) {
                candidates.push(enemy);
            }
        }
        return selectByPriority(candidates, 'mostClustered' as TargetPriority, this.x, this.y);
    }

    private fire(target: Enemy): void {
        const proj = new Projectile(this.scene, this.x, this.y - 15, {
            damage: this.getDamage(),
            speed: TREBUCHET.projectileSpeed,
            piercing: false,
            projectileType: 'rock_heavy',
            targetX: target.x,
            targetY: target.y,
            lobbing: true,
            aoeRadius: this.getAoeRadius(),
        });
        this.projectiles.push(proj);
    }

    draw(): void {
        this.sprite.setTexture('building_trebuchet');
        this.graphics.clear();
        if (this.level > 1) {
            this.graphics.fillStyle(0xffd700, 1);
            for (let i = 0; i < Math.min(this.level - 1, 5); i++) {
                this.graphics.fillCircle(this.x - 6 + i * 3, this.y + 8, 1.5);
            }
        }
    }

    destroy(): void {
        this.graphics.destroy();
        this.sprite.destroy();
    }
}
